import React from 'react';
import { ttCss } from './styles';
import { GeneratedSocialPostResult } from 'src/api/types/social-types';

type Maybe<T> = T | null | undefined;

export interface TtCounts {
  likes?: number;
  comments?: number;
  shares?: number;
  bookmarks?: number;
  views?: number | string;
}

export interface TikTokPostPreviewProps {
  data: GeneratedSocialPostResult;
  counts?: TtCounts;
  injectCss?: boolean;
  profileName?: string; // Display name
  handle?: string; // @handle
  when?: string; // e.g., "Just now"
  avatarUrl?: string;
}

function linkify(text: string) {
  const urlRegex =
    /((https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/[^\s]*)?)/gi;
  const lines = (text || '').split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line.split(urlRegex).map((chunk, j) => {
        const looksLikeUrl =
          /^https?:\/\//i.test(chunk) || /^[a-z0-9-]+\.[a-z]{2,}/i.test(chunk);
        if (looksLikeUrl) {
          const href = /^https?:\/\//i.test(chunk) ? chunk : `https://${chunk}`;
          return (
            <a
              key={`${i}-${j}`}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="ttp-link">
              {chunk}
            </a>
          );
        }
        return <React.Fragment key={`${i}-${j}`}>{chunk}</React.Fragment>;
      })}
      {i < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
}

export default function TikTokPostPreview({
  data,
  counts,
  injectCss = true,
  profileName,
  handle,
  when = 'Just now',
  avatarUrl = 'https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg',
}: TikTokPostPreviewProps) {
  const post = data.post;
  const name = profileName || post.location?.name || 'Creator';
  const userHandle = handle || '@username';

  const likes = counts?.likes ?? 0;
  const comments = counts?.comments ?? 0;
  const shares = counts?.shares ?? 0;
  const bookmarks = counts?.bookmarks ?? 0;
  const views = counts?.views ?? 0;

  // Prefer video (9:16), then image
  const videoSrc: Maybe<string> = post.videoDataUrl;
  const posterSrc: Maybe<string> = post.imageLinkUrl || post.image?.link_url;
  const imageSrc: Maybe<string> = post.imageLinkUrl || post.image?.link_url;

  const hasVideo = Boolean(videoSrc);
  const hasImage = !hasVideo && Boolean(imageSrc);

  const hasLink = Boolean(post.metadata?.has_link && post.cta?.url);

  return (
    <>
      {injectCss && <style>{ttCss}</style>}

      <main className="ttp-container" role="main">
        <article className="ttp-card" aria-label="TikTok-like post preview">
          {/* Header */}
          <header className="ttp-header">
            <img src={avatarUrl} alt="Profile" className="ttp-avatar" />
            <div className="ttp-header-main">
              <div className="ttp-name-row">
                <a className="ttp-name" role="button" tabIndex={0}>
                  {name}
                </a>
                <span className="ttp-handle">{userHandle}</span>
                <span className="ttp-dot">·</span>
                <span className="ttp-when">{when}</span>
              </div>
              {/* Optional subtitle line (category/city) */}
              {post.location?.city ? (
                <div className="ttp-sub">{post.location.city}</div>
              ) : null}
            </div>
            <button className="ttp-follow" type="button">
              Follow
            </button>
          </header>

          {/* Media: 9:16 video preferred */}
          {hasVideo && (
            <section className="ttp-media-wrap">
              <div className="ttp-aspect">
                <video
                  className="ttp-media"
                  src={videoSrc as string}
                  poster={posterSrc || undefined}
                  playsInline
                  muted
                  controls
                  preload="metadata"
                />
              </div>
            </section>
          )}

          {/* Image fallback (center-cropped into 9:16 box) */}
          {!hasVideo && hasImage && (
            <section className="ttp-media-wrap">
              <div className="ttp-aspect">
                <img
                  className="ttp-media"
                  src={imageSrc as string}
                  alt={post.image?.alt || ''}
                />
              </div>
            </section>
          )}

          {/* Caption */}
          <div className="ttp-caption">{linkify(post.body || '')}</div>

          {/* Mentions */}
          {post.mentions?.length ? (
            <div className="ttp-mentions">
              {post.mentions.map(m => (
                <span key={m} className="ttp-mention">
                  {m}
                </span>
              ))}
            </div>
          ) : null}

          {/* Hashtags */}
          {post.hashtags?.length ? (
            <div className="ttp-tags">
              {post.hashtags.map(t => (
                <a key={t} className="ttp-tag">
                  {t}
                </a>
              ))}
            </div>
          ) : null}

          {/* Link note (TikTok doesn’t show rich link previews in feed) */}
          {hasLink && (
            <div className="ttp-link-note">
              <a
                href={post.cta.url}
                target="_blank"
                rel="noreferrer"
                className="ttp-link">
                {post.cta.url}
              </a>
            </div>
          )}

          {/* Counters */}
          <div className="ttp-counters">
            <span>
              <b>
                {typeof views === 'number'
                  ? Intl.NumberFormat().format(views)
                  : views}
              </b>{' '}
              views
            </span>
            <span>
              <b>{Intl.NumberFormat().format(likes)}</b> likes
            </span>
            <span>
              <b>{Intl.NumberFormat().format(comments)}</b> comments
            </span>
            <span>
              <b>{Intl.NumberFormat().format(shares)}</b> shares
            </span>
            <span>
              <b>{Intl.NumberFormat().format(bookmarks)}</b> saves
            </span>
          </div>

          {/* Actions (static UI) */}
          <div className="ttp-actions">
            <button className="ttp-action" aria-label="Like">
              <svg viewBox="0 0 24 24" className="ttp-icon">
                <path d="M16.697 5.5c-1.222-.06-2.679.51-3.89 2.16l-.805 1.09-.806-1.09C9.984 6.01 8.526 5.44 7.304 5.5c-1.243.07-2.349.78-2.91 1.91-.552 1.12-.633 2.78.479 4.82 1.074 1.97 3.257 4.27 7.129 6.61 3.87-2.34 6.052-4.64 7.126-6.61 1.111-2.04 1.03-3.7.477-4.82-.561-1.13-1.666-1.84-2.908-1.91z" />
              </svg>
              <span>Like</span>
            </button>
            <button className="ttp-action" aria-label="Comment">
              <svg viewBox="0 0 24 24" className="ttp-icon">
                <path
                  d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              <span>Comment</span>
            </button>
            <button className="ttp-action" aria-label="Share">
              <svg viewBox="0 0 24 24" className="ttp-icon">
                <path d="M12 2.59l5.7 5.7-1.41 1.42L13 6.41V16h-2V6.41l-3.3 3.3-1.41-1.42L12 2.59zM21 15l-.02 3.51c0 1.38-1.12 2.49-2.5 2.49H5.5C4.11 21 3 19.88 3 18.5V15h2v3.5c0 .28.22.5.5.5h12.98c.28 0 .5-.22.5-.5L19 15h2z" />
              </svg>
              <span>Share</span>
            </button>
            <button className="ttp-action" aria-label="Save">
              <svg viewBox="0 0 24 24" className="ttp-icon">
                <path d="M4 4.5C4 3.12 5.119 2 6.5 2h11C18.881 2 20 3.12 20 4.5v18.44l-8-5.71-8 5.71V4.5z" />
              </svg>
              <span>Save</span>
            </button>
          </div>
        </article>
      </main>
    </>
  );
}
